import type { SampleFolderMeta } from "../../../../src/lib/audio";
import type { AdamCommand, AdamCommandContext, AdamCommandResult } from "./Command";
import type { DeletedFolderPayload, DeletedSamplePayload } from "./sampleBridge";

function requireSamples(context: AdamCommandContext) {
  const samples = context.samples;
  if (!samples) {
    throw new Error("Adam sample bridge is not registered");
  }
  return samples;
}

/** Rename an imported sample asset (host library). */
export class RenameSampleCommand implements AdamCommand {
  readonly id = "renameSample";
  readonly label = "Rename sample";
  /** Sample library is host state, not project document dirty. */
  readonly affectsDocument = false;

  constructor(
    readonly sampleId: string,
    readonly from: string,
    readonly to: string,
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    requireSamples(context).renameSample(this.sampleId, this.to);
    return { changed: "samples" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireSamples(context).renameSample(this.sampleId, this.from);
    return { changed: "samples" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }

  canMergeWith(command: AdamCommand): boolean {
    return command instanceof RenameSampleCommand && command.sampleId === this.sampleId;
  }

  merge(command: AdamCommand): AdamCommand {
    if (!(command instanceof RenameSampleCommand) || !this.canMergeWith(command)) {
      return this;
    }
    return new RenameSampleCommand(this.sampleId, this.from, command.to);
  }
}

/**
 * Delete an imported sample. Payload (meta + pcm) is captured before execute
 * so undo can restore through page reload of the session.
 */
export class DeleteSampleCommand implements AdamCommand {
  readonly id = "deleteSample";
  readonly label = "Delete sample";
  readonly affectsDocument = false;
  private applied = false;

  constructor(readonly payload: DeletedSamplePayload) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    requireSamples(context).applyDeleteSample(this.payload);
    this.applied = true;
    return { changed: "samples" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    if (!this.applied) {
      throw new Error("DeleteSampleCommand has nothing to undo");
    }
    requireSamples(context).restoreSample(this.payload);
    this.applied = false;
    return { changed: "samples" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}

/** Move a sample into a folder (or Library root). */
export class MoveSampleCommand implements AdamCommand {
  readonly id = "moveSample";
  readonly label = "Move sample";
  readonly affectsDocument = false;

  constructor(
    readonly sampleId: string,
    readonly fromFolderId: string | null,
    readonly toFolderId: string | null,
    readonly fromSortKey: number | undefined,
    readonly toSortKey: number | undefined,
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    requireSamples(context).moveSample(this.sampleId, this.toFolderId, this.toSortKey);
    return { changed: "samples" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireSamples(context).moveSample(this.sampleId, this.fromFolderId, this.fromSortKey);
    return { changed: "samples" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}

/** Create a sample library folder. */
export class CreateFolderCommand implements AdamCommand {
  readonly id = "createSampleFolder";
  readonly label = "Create folder";
  readonly affectsDocument = false;
  private folder: SampleFolderMeta | null = null;

  constructor(
    readonly draft: {
      id: string;
      name: string;
      parentId?: string | null;
      createdAt?: string;
    },
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    this.folder = requireSamples(context).createFolder(
      this.folder
        ? {
            id: this.folder.id,
            name: this.folder.name,
            parentId: this.folder.parentId,
            createdAt: this.folder.createdAt,
            sortKey: this.folder.sortKey,
          }
        : this.draft,
    );
    return { changed: "samples" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    if (!this.folder) {
      throw new Error("CreateFolderCommand has nothing to undo");
    }
    requireSamples(context).applyDeleteFolder({ folders: [this.folder], samples: [] });
    return { changed: "samples" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}

/** Rename a sample library folder. */
export class RenameFolderCommand implements AdamCommand {
  readonly id = "renameSampleFolder";
  readonly label = "Rename folder";
  readonly affectsDocument = false;

  constructor(
    readonly folderId: string,
    readonly from: string,
    readonly to: string,
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    requireSamples(context).renameFolder(this.folderId, this.to);
    return { changed: "samples" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireSamples(context).renameFolder(this.folderId, this.from);
    return { changed: "samples" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }

  canMergeWith(command: AdamCommand): boolean {
    return command instanceof RenameFolderCommand && command.folderId === this.folderId;
  }

  merge(command: AdamCommand): AdamCommand {
    if (!(command instanceof RenameFolderCommand) || !this.canMergeWith(command)) {
      return this;
    }
    return new RenameFolderCommand(this.folderId, this.from, command.to);
  }
}

/** Reparent / reorder a sample library folder. */
export class MoveFolderCommand implements AdamCommand {
  readonly id = "moveSampleFolder";
  readonly label = "Move folder";
  readonly affectsDocument = false;

  constructor(
    readonly folderId: string,
    readonly fromParentId: string | null,
    readonly toParentId: string | null,
    readonly fromSortKey: number,
    readonly toSortKey: number | undefined,
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    requireSamples(context).moveFolder(this.folderId, this.toParentId, this.toSortKey);
    return { changed: "samples" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireSamples(context).moveFolder(this.folderId, this.fromParentId, this.fromSortKey);
    return { changed: "samples" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}

/**
 * Delete a folder subtree (folders + samples). Payload captured before execute.
 */
export class DeleteFolderCommand implements AdamCommand {
  readonly id = "deleteSampleFolder";
  readonly label = "Delete folder";
  readonly affectsDocument = false;
  private applied = false;

  constructor(readonly payload: DeletedFolderPayload) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    requireSamples(context).applyDeleteFolder(this.payload);
    this.applied = true;
    return { changed: "samples" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    if (!this.applied) {
      throw new Error("DeleteFolderCommand has nothing to undo");
    }
    requireSamples(context).restoreFolder(this.payload);
    this.applied = false;
    return { changed: "samples" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}
